import {
	FetchMeResponseSchema,
	LoginResponse,
	LoginResponseSchema,
	User,
} from "@/pages/Public/pages/Login/schema/login";
import { api } from "@/redux/api";
import { StatusResponse, StatusResponseSchema } from "@/schemas/status-response";
import {
	GetAllPermissionsResponse,
	GetAllRolesResponse,
	GetAllSendersUsersResponse,
	GetAllUsersResponse,
	UpdateLinkedinSettingsBody,
	UpdateRoleBody,
	UpdateRolePermissionsBody,
	UpdateUserBody,
	UpdateUserRoleBody,
} from "@/types/admin";
import { RequestPaginationParams } from "@/types/request-params";
import { CampaignAction } from "@/pages/Private/pages/Account/schema/pendingSchedules";

import {
	CreateRoleRequest,
	CreateRoleRequestSchema,
	CreateRoleResponseSchema,
	DeleteRoleRequestSchema,
	DeleteRoleResponseSchema,
	GetRolesResponseSchema,
	UpdateRolePermissionResponseSchema,
	UpdateRoleResponseSchema,
	UpdateRoleSchema,
	UserRole,
} from "../../pages/UserRoles/schema/roles";
import { DeleteUserSchema, DeletedUser } from "../../pages/Users/schema/delete-user";
import {
	CheckConnectionResponseSchema,
	GetAllSendersUsersResponseSchema,
	GetAllUsersResponseSchema,
	GetCompanyUsersResponseSchema,
	GetSenderCandidatesResponseSchema,
	type CheckConnectionQuery,
	type CheckConnectionResponse,
	type GetCompanyUsersResponse,
	type GetSenderCandidatesResponse,
} from "../../pages/Users/schema/get-users";

import {
	InviteRequest,
	InviteRequestSchema,
	InviteResponseSchema,
} from "../../pages/Users/schema/invite-user";
import {
	GetPermissionsResponseSchema,
	UpdateRolePermissionsRequestSchema,
} from "../../pages/Users/schema/permissions";
import { UpdateUserRolesSchema } from "../../pages/Users/schema/update-user";

export const adminApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getUsers: builder.query<GetAllUsersResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "users",
				...(params && {
					params: {
						page: params.page,
						limit: params.limit,
						...(params.fieldName ? { fieldName: params.fieldName } : {}),
						...(params.searchValue ? { value: params.searchValue } : {}),
						...(params.companyId ? { companyId: params.companyId } : {}),
						...(params.sorting ? { ...params.sorting } : {}),
					},
				}),
				responseSchema: GetAllUsersResponseSchema,
			}),
			providesTags: ["Users"],
		}),
		getSendersUsers: builder.query<GetAllSendersUsersResponse, void>({
			query: () => ({
				url: "users/sending-users",
				responseSchema: GetAllSendersUsersResponseSchema,
			}),
			providesTags: ["Users"],
		}),
		getSenderCandidates: builder.query<GetSenderCandidatesResponse, void>({
			query: () => ({
				url: "users/sender-candidates",
				responseSchema: GetSenderCandidatesResponseSchema,
			}),
			providesTags: ["Users"],
		}),
		getCompanyUsers: builder.query<GetCompanyUsersResponse, void>({
			query: () => ({
				url: "users/company-users",
				responseSchema: GetCompanyUsersResponseSchema,
			}),
			providesTags: ["Users"],
		}),
		loginAs: builder.mutation<LoginResponse, number>({
			query: (id) => ({
				url: "users/loginAs",
				method: "POST",
				body: {
					id,
				},
				responseSchema: LoginResponseSchema,
			}),
		}),
		inviteUser: builder.mutation<Partial<User>, InviteRequest>({
			query: (body) => ({
				url: "users/invite",
				method: "POST",
				body,
				requestSchema: InviteRequestSchema,
				responseSchema: InviteResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		updateUser: builder.mutation<User, UpdateUserBody>({
			query: ({ body, id }) => ({
				url: `users/${id}`,
				method: "PATCH",
				body,
				responseSchema: FetchMeResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		updateUserLinkedinSettings: builder.mutation<User, UpdateLinkedinSettingsBody>({
			query: ({ body, id }) => ({
				url: `users/${id}`,
				method: "PATCH",
				body,
				responseSchema: FetchMeResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		deleteUser: builder.mutation<DeletedUser, { id: number; campaignActions?: CampaignAction[] }>({
			query: ({ id, campaignActions }) => ({
				url: `users/${id}`,
				method: "DELETE",
				...(campaignActions ? { body: { campaignActions } } : {}),
				responseSchema: DeleteUserSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		resetUserPassword: builder.mutation<StatusResponse, number>({
			query: (id) => ({
				url: `users/${id}/reset-password`,
				method: "POST",
				responseSchema: StatusResponseSchema,
			}),
		}),
		assignUserRoles: builder.mutation<User, UpdateUserRoleBody>({
			query: ({ id, body }) => ({
				url: `users/${id}/roles`,
				method: "POST",
				body,
				requestSchema: UpdateUserRolesSchema,
				responseSchema: FetchMeResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		unAssignUserRoles: builder.mutation<User, UpdateUserRoleBody>({
			query: ({ id, body }) => ({
				url: `users/${id}/roles`,
				method: "DELETE",
				body,
				requestSchema: UpdateUserRolesSchema,
				responseSchema: FetchMeResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		getRoles: builder.query<GetAllRolesResponse, void>({
			query: () => ({
				url: "/roles",
				responseSchema: GetRolesResponseSchema,
			}),
			providesTags: ["Roles"],
		}),
		createRole: builder.mutation<UserRole, CreateRoleRequest>({
			query: (body) => ({
				url: `/roles`,
				method: "POST",
				body,
				requestSchema: CreateRoleRequestSchema,
				responseSchema: CreateRoleResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		reinviteUser: builder.mutation<Partial<User>, number>({
			query: (id) => ({
				url: `users/${id}/reinvite`,
				method: "POST",
				responseSchema: InviteResponseSchema,
			}),
			invalidatesTags: ["Users"],
		}),
		updateUserLinkedinAllowed: builder.mutation<
			Partial<User>,
			{ id: number; campaignActions?: CampaignAction[] }
		>({
			query: ({ id, campaignActions }) => ({
				url: `users/${id}/linkedin-allowed`,
				method: "PATCH",
				...(campaignActions ? { body: { campaignActions } } : {}),
				responseSchema: FetchMeResponseSchema,
			}),
			invalidatesTags: ["Slots"],
		}),
		updateRole: builder.mutation<Partial<UserRole>, UpdateRoleBody>({
			query: ({ id, body }) => ({
				url: `/roles/${id}`,
				method: "PATCH",
				body,
				requestSchema: UpdateRoleSchema,
				responseSchema: UpdateRoleResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		deleteRole: builder.mutation<UserRole, number>({
			query: (id) => ({
				url: `/roles/${id}`,
				method: "DELETE",
				requestSchema: DeleteRoleRequestSchema,
				responseSchema: DeleteRoleResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		assignPermissions: builder.mutation<UserRole, UpdateRolePermissionsBody>({
			query: ({ id, body }) => ({
				url: `/roles/${id}/permissions`,
				method: "POST",
				body,
				requestSchema: UpdateRolePermissionsRequestSchema,
				responseSchema: UpdateRolePermissionResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		unassignPermissions: builder.mutation<UserRole, UpdateRolePermissionsBody>({
			query: ({ id, body }) => ({
				url: `/roles/${id}/permissions`,
				method: "DELETE",
				body,
				requestSchema: UpdateRolePermissionsRequestSchema,
				responseSchema: UpdateRolePermissionResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		getPermissions: builder.query<GetAllPermissionsResponse, void>({
			query: () => ({
				url: "/permissions",
				responseSchema: GetPermissionsResponseSchema,
			}),
		}),
		checkLinkedinConnection: builder.query<CheckConnectionResponse, CheckConnectionQuery>({
			query: (params) => {
				const searchParams = new URLSearchParams();

				for (const id of params.userIds) {
					searchParams.append("userIds", String(id));
				}

				if (params.linkedinUrl) {
					searchParams.append("linkedinUrl", params.linkedinUrl);
				}

				if (params.urn) {
					searchParams.append("urn", params.urn);
				}

				if (params.listPersonId) {
					searchParams.append("listPersonId", String(params.listPersonId));
				}

				return {
					url: `users/check-connection?${searchParams.toString()}`,
					responseSchema: CheckConnectionResponseSchema,
				};
			},
		}),
	}),
});

export const {
	useGetUsersQuery,
	useLazyGetUsersQuery,
	useUpdateUserMutation,
	useLoginAsMutation,
	useDeleteUserMutation,
	useReinviteUserMutation,
	useInviteUserMutation,
	useResetUserPasswordMutation,
	useAssignUserRolesMutation,
	useUnAssignUserRolesMutation,
	useGetRolesQuery,
	useCreateRoleMutation,
	useUpdateRoleMutation,
	useAssignPermissionsMutation,
	useUnassignPermissionsMutation,
	useGetPermissionsQuery,
	useDeleteRoleMutation,
	useUpdateUserLinkedinAllowedMutation,
	useUpdateUserLinkedinSettingsMutation,
	useGetSendersUsersQuery,
	useGetSenderCandidatesQuery,
	useGetCompanyUsersQuery,
	useLazyCheckLinkedinConnectionQuery,
} = adminApi;
export const adminApiReducer = adminApi.reducer;
export const adminApiMiddleware = adminApi.middleware;
